import debounce from "lodash/debounce";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const mdBreakpoint = window.matchMedia("(min-width: 768px)");
const activationPhaseDuration = 0.35;
const holdPhaseDuration = 0.65;
const imageFadePhaseDuration = 0.2;
const imageHoldPhaseDuration = 0.8;

const getContentHeight = (content) => {
  const styles = window.getComputedStyle(content);
  const margin = parseFloat(styles.marginTop) + parseFloat(styles.marginBottom);

  return content.scrollHeight + margin;
};

const measureContentHeights = (el) => {
  el.querySelectorAll(".scroll-content").forEach((content) => {
    content.setAttribute("data-height", getContentHeight(content));
  });
};

const setupHeights = (el) => {
  const mediaColumn = el.querySelector(".media-column");
  const contentColumn = el.querySelector(".content-column");
  if (!contentColumn) return;

  const lastElement = contentColumn.querySelector(
    ".scroll-sections-placeholder .scroll-section-placeholder:last-child",
  );

  if (!lastElement) return;

  const isTwoColumn = mdBreakpoint.matches;
  const contentHeight = contentColumn.offsetHeight;
  const mediaHeight = mediaColumn?.offsetHeight ?? 0;

  if (isTwoColumn && mediaColumn && contentHeight > mediaHeight) {
    const offset = contentColumn.offsetHeight - lastElement.offsetHeight;
    contentColumn.style.paddingBottom = `${offset / 4}px`;
  } else {
    contentColumn.style.paddingBottom = null;
  }
};

document.addEventListener("DOMContentLoaded", () => {
  const brandGreen = getComputedStyle(document.documentElement).getPropertyValue("--color-brand-green").trim();
  const elements = Array.from(document.querySelectorAll(".element-scrollableexpandingcontent"));

  const syncMeasurements = () => {
    elements.forEach((el) => {
      measureContentHeights(el);
      setupHeights(el);
    });
  };

  elements.forEach((el) => {
    const hasGlobalImage = el.querySelector(".element-container")?.dataset.hasGlobalImage === "true";
    const contents = Array.from(el.querySelectorAll(".scroll-content"));
    const sections = Array.from(el.querySelectorAll(".scroll-section"));
    const placeholders = Array.from(el.querySelectorAll(".scroll-section-placeholder"));

    gsap.set(contents, { height: 0 });
    gsap.set(
      sections.map((section) => section.querySelector("h2")),
      { color: "" },
    );

    placeholders.forEach((placeholder, index) => {
      const section = sections[index];
      const content = contents[index];
      const heading = section?.querySelector("h2");
      const sectionImage = section ? el.querySelector(`.section-image[data-pos="${section.dataset.index}"]`) : null;

      if (!section || !content || !heading) return;

      gsap
        .timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            trigger: placeholder,
            start: () => (mdBreakpoint.matches ? "top 35%" : "top 80%"),
            end: () => (mdBreakpoint.matches ? "bottom 35%" : "bottom 55%"),
            scrub: true,
            invalidateOnRefresh: true,
          },
        })
        .to(content, { height: () => Number(content.dataset.height), duration: activationPhaseDuration })
        .to(heading, { color: brandGreen, duration: 0.01 }, 0)
        .to({}, { duration: holdPhaseDuration });

      if (!hasGlobalImage && sectionImage && index > 0) {
        gsap
          .timeline({
            defaults: { ease: "none" },
            scrollTrigger: {
              trigger: placeholder,
              start: "top 35%",
              end: "bottom 35%",
              scrub: true,
              invalidateOnRefresh: true,
            },
          })
          .to(sectionImage, { opacity: 1, duration: imageFadePhaseDuration })
          .to({}, { duration: imageHoldPhaseDuration });
      }
    });
  });

  const handleRefreshInit = () => {
    syncMeasurements();
  };

  ScrollTrigger.addEventListener("refreshInit", handleRefreshInit);
  syncMeasurements();
  ScrollTrigger.refresh();

  let lastWidth = document.body.clientWidth;

  const debouncedSetupHeights = debounce(() => {
    syncMeasurements();
    ScrollTrigger.refresh();
  }, 250);

  const resizeObserver = new ResizeObserver(() => {
    const currentWidth = document.body.clientWidth;
    if (currentWidth === lastWidth) return;
    lastWidth = currentWidth;
    debouncedSetupHeights();
  });
  resizeObserver.observe(document.body);

  window.addEventListener("unload", () => {
    ScrollTrigger.removeEventListener("refreshInit", handleRefreshInit);
    resizeObserver.disconnect();
  });
});
